<script setup>
// @ts-check

import { createRandomUuid } from '@/helpers/functions'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import DateField from './DateField.vue'
import { stringToDate } from '@/helpers/dates'

const props = defineProps({
  label: {
    type: String,
    default: null
  },
  errorMessages: {
    /** @type {import('vue').PropType<string | readonly string[]>} */
    type: Array,
    default: () => []
  },
  hint: {
    type: String,
    default: null
  },
  required: {
    type: Boolean
  }
})

defineOptions({
  name: 'DateRangeField'
})

/** @type {import('vue').ModelRef<[?string, ?string]>} */
const range = defineModel({
  type: Array,
  default: () => [null, null]
})

const { t } = useI18n()

const id = createRandomUuid()

const hasError = computed(() => !!props.errorMessages?.length)

const messages = computed(() => {
  if (props.errorMessages?.length) {
    return props.errorMessages
  }
  if (props.hint) {
    return props.hint
  }
  return []
})

const hasMessages = computed(() => messages.value.length > 0)

const min = computed(() =>
  range.value[0] ? stringToDate(range.value[0]) : null
)

const max = computed(() =>
  range.value[1] ? stringToDate(range.value[1]) : null
)
</script>

<template>
  <div class="relative">
    <VLabel
      v-if="label"
      :class="{ 'text-danger': hasError, required }"
      class="responsive-label absolute! -top-0.5 z-10 bg-white! px-1"
      :for="id"
      :text="label"
    />
    <div class="flex flex-row items-end pt-1.5 md:pt-5">
      <DateField
        :id
        :error="hasError"
        :aria-label="t('dates.select_start_date')"
        :model-value="range[0]"
        clearable
        class="start-date-field"
        :max
        @update:model-value="range = [$event, range[1]]"
      />
      <DateField
        :error="hasError"
        :model-value="range[1]"
        :aria-label="t('dates.select_end_date')"
        clearable
        class="end-date-field"
        :min
        @update:model-value="range = [range[0], $event]"
      />
    </div>

    <div
      v-if="hasMessages"
      class="v-input__details"
      role="alert"
      aria-live="polite"
    >
      <VMessages
        :class="{ 'text-danger opacity-100': hasError }"
        active
        :messages
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
@reference '../../styles/main.css';

.start-date-field {
  :deep(.v-field__outline__end) {
    @apply rounded-r-none;
  }
}

.end-date-field {
  :deep(.v-field__outline__start) {
    @apply rounded-l-none;
  }
}
</style>
